import * as Collapsible from '@radix-ui/react-collapsible';
import { capitalize, isEmpty, round, slice, take } from 'lodash';
import pluralize from 'pluralize';
import { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { Icon } from '@/components/icon';
import { Image } from '@/components/image';
import { Checkbox } from '@/components/inputs/check-box';
import { Toggle } from '@/components/inputs/toggle';
import { CongestionChart } from '@/components/modules/charts/congestion-chart';
import { Text } from '@/components/text';
import { UserMessage } from '@/components/ui/user-message';
import { VisuallyHidden } from '@/components/visually-hidden';
import { CONGESTION_ATTRIBUTES, PORT_CONNECTION_TYPES } from '@/lib/constants';
import {
  activeCarrierFilterState,
  carriersIsOpenState,
  connectionsDirectionState,
  portsFiltersState,
  selectedPortConnectionsCountState,
  showConnectionsState,
} from '@/state/ports';
import { colors } from '@/theme/constants';
import { DistanceUnitsType, LocationInterface, PortInterface } from '@/types';
import { PortDataApiInterface } from '@/types/api-types';
import { createPortPageUrl, getPreferredIdentifier } from '@/utils/helpers';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { getLocationUrlQueryValue } from '@/utils/places';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { getDistanceFromSearchOrigin, getDistanceInSelectedUnits } from '../map/utils';
import { portsListStyles } from './ports-list';

const NUMBER_OF_CARRIERS_TO_SHOW = 8;

const PortDetails = ({
  selectedPort,
  selectedPortDetails,
  searchOrigin,
  handleSelectedPortClose,
  handleConnectionsSelect,
  handleNearbyPortsSearch,
  handleConnectionsDirectionChange,
  isSearchingNearbyPorts,
  distanceUnits,
}: {
  selectedPort: PortInterface;
  selectedPortDetails?: PortDataApiInterface | null;
  handleSelectedPortClose: () => void;
  handleConnectionsSelect: () => void;
  handleNearbyPortsSearch: (portName: string) => void;
  handleConnectionsDirectionChange: (value: boolean) => void;
  searchOrigin?: LocationInterface | null;
  isSearchingNearbyPorts: boolean;
  distanceUnits: DistanceUnitsType;
}) => {
  const [carriersIsOpen, setCarriersIsOpen] = useRecoilState(carriersIsOpenState);
  const connectionsDirection = useRecoilValue(connectionsDirectionState);
  const showConnections = useRecoilValue(showConnectionsState);
  const connectionsCount = useRecoilValue(selectedPortConnectionsCountState);
  const portsFilters = useRecoilValue(portsFiltersState);
  const activeCarrierFilter = useRecoilValue(activeCarrierFilterState);

  // These buttons are just links so we just show a loader briefly when clicked
  const [routeFromButtonIsLoading, setRouteFromButtonIsLoading] = useState(false);
  const [portDetailsButtonIsLoading, setPortDetailsButtonIsLoading] = useState(false);

  const handleCarriersButtonClick = () => {
    setCarriersIsOpen(!carriersIsOpen);

    if (!carriersIsOpen) {
      // Analytics Event
      trackUser.event('Show All Port Carriers');
    }
  };

  const handleRouteFromButtonClick = () => {
    setRouteFromButtonIsLoading(!routeFromButtonIsLoading);

    trackUser.event('Route From Port', { port: selectedPort.name });
  };

  const handlePortDetailsButtonClick = () => {
    setPortDetailsButtonIsLoading(!portDetailsButtonIsLoading);

    trackUser.event('See Port Details', { port: selectedPort.name });
  };

  const portPageUrl = createPortPageUrl(selectedPort.ids);

  const distanceFromSearchOrigin = searchOrigin ? getDistanceFromSearchOrigin(selectedPort, searchOrigin) : 0;

  const preferredIdentifier = getPreferredIdentifier(selectedPort.ids);

  const portUrlQueryValue = getLocationUrlQueryValue(selectedPort);

  const { closeButton } = portsListStyles();

  const {
    base,
    portInfo,
    portInfoItem,
    contentWrapper,
    connectionsToggle,
    pseudoLink,
    carrierText,
    carriersContent,
    carriersShowMore,
    distanceFromOrigin,
    portInfoItemText,
    searchOriginExtras,
    actions,
  } = styles();

  return (
    <div className={base()}>
      <div className={contentWrapper()}>
        {searchOrigin ? (
          <div className={searchOriginExtras()}>
            <Button
              onClick={() => handleSelectedPortClose()}
              variant="secondaryLight"
              size="small"
              iconLeft="arrow-left-alt"
            >
              Back to results
            </Button>
            <div className="overflow-hidden">
              <div className={distanceFromOrigin()}>
                <Icon name="map-marker-radius" size="5" color="grey.600" className="mr-xs" />
                <Text as="span" variant="secondary" className="max-w-56 truncate font-medium">
                  {`${round(getDistanceInSelectedUnits(distanceFromSearchOrigin, distanceUnits), 1)} ${distanceUnits}`}{' '}
                  from {searchOrigin.name}
                </Text>
              </div>
            </div>
          </div>
        ) : (
          <button onClick={() => handleSelectedPortClose()} className={closeButton()} type="button">
            <VisuallyHidden>Close port details</VisuallyHidden>
            <Icon name="close" size="md" color="white" />
          </button>
        )}
        <div>
          <Text
            variant="label"
            className="mb-2"
            style={{
              color: getTransportColor(selectedPort.type),
            }}
          >
            {capitalize(selectedPort.type)}
          </Text>
          <Heading variant="h3" className="pr-12">
            {selectedPort.name}
          </Heading>
        </div>

        {Boolean(portsFilters?.carrier) && (
          <UserMessage
            variant="warning"
            heading={`Only showing information for ${portsFilters.carrier?.label}.`}
            body="To see all information, clear the carrier filter"
            size="sm"
            className="!my-3"
          />
        )}

        <ul className={portInfo()}>
          <li className={portInfoItem()}>
            <Text variant="label" className={portInfoItemText()}>
              {preferredIdentifier?.type}
            </Text>
            <Text variant="secondary">{preferredIdentifier?.value}</Text>
          </li>
          <li className={portInfoItem()}>
            <Text variant="label" className={portInfoItemText()}>
              Daily departures
            </Text>
            <Text variant="secondary">
              {Math.round(activeCarrierFilter?.frequency.value || 0) || selectedPort.frequency}
            </Text>
          </li>
          <li className={portInfoItem()}>
            <Text variant="label" className={portInfoItemText()}>
              Direct {connectionsDirection} connections
            </Text>
            <Text variant="secondary">{connectionsCount}</Text>
          </li>
        </ul>

        <div className={connectionsToggle()}>
          <Image src="/assets/connections-example.png" alt="Example of connections" className="w-[140px] rounded" />
          <div className="ml-4">
            <Checkbox value="show-connections" isSelected={showConnections} onChange={handleConnectionsSelect}>
              <span className={pseudoLink()}>
                Show {connectionsCount} direct {pluralize('connection', connectionsCount)}
              </span>
            </Checkbox>
            <Text variant="secondary" className="mb-0 mt-2 opacity-75">
              This will show all the other ports around the world that are connected by direct services.
            </Text>
            <div className="-ml-1 mt-4 inline-block">
              <Toggle
                legend="Connections"
                onLabel="Outbound"
                offLabel="Inbound"
                name="outbound-inbound-connections-toggle"
                onChange={handleConnectionsDirectionChange}
                on={connectionsDirection === PORT_CONNECTION_TYPES.OUTBOUND}
                disabled={!showConnections}
              />
            </div>
          </div>
        </div>

        {selectedPortDetails?.congestion && !isEmpty(selectedPortDetails.congestion.categories) && (
          <div className="mb-4 mt-7 pb-6">
            <Heading variant="h6" as="h3" className="mb-base">
              Congestion data
            </Heading>

            <Text variant="sm" className="mb-6 text-grey-600">
              Historical trends of both operational time and anchorage waiting time at {selectedPort.name}. The unit of
              measurement is in &apos;Days&apos; and the data relates to Imports only.
            </Text>

            <CongestionChart
              id="port-insights-congestion-chart"
              data={selectedPortDetails?.congestion}
              chartColors={[colors.charts.line[7]]}
              radioTabsClassName="!mb-4"
              chartWrapperClassName="-mx-4 xl:-mx-5"
              selectBoxVariant="small"
              attributesToInclude={[CONGESTION_ATTRIBUTES.DAYS_TOTAL]}
              showLatestDaysTotal
              chartClassName="h-[88px] min-h-none"
              chartSettings={{
                chart: {
                  margin: {
                    top: 4,
                    right: 40,
                    bottom: 0,
                    left: 0,
                  },
                },
                axes: {
                  xAxis: {
                    interval: 8,
                  },
                },
                grid: {
                  horizontal: false,
                  vertical: true,
                },
                legend: {
                  hide: true,
                },
              }}
            />
          </div>
        )}

        {Boolean(selectedPort.carrierCount) && (
          <div className="mt-6">
            <Collapsible.Root open={carriersIsOpen} onOpenChange={handleCarriersButtonClick}>
              <Heading variant="h6" as="h3" className="mr-base">
                {selectedPort.carrierCount} {pluralize('carrier', selectedPort.carrierCount)}
              </Heading>

              <div className={carriersContent()}>
                <div>
                  {take(selectedPortDetails?.carriers, NUMBER_OF_CARRIERS_TO_SHOW).map((carrier) => (
                    <span
                      key={carrier.name}
                      className={carrierText({
                        carriersIsOpen,
                        noMoreToShow: (selectedPort.carrierCount || 0) <= NUMBER_OF_CARRIERS_TO_SHOW,
                      })}
                    >
                      {carrier.name}
                    </span>
                  ))}
                </div>
                <Collapsible.Content>
                  {slice(selectedPortDetails?.carriers, NUMBER_OF_CARRIERS_TO_SHOW).map((carrier) => (
                    <span key={carrier.name} className={carrierText()}>
                      {carrier.name}
                    </span>
                  ))}
                </Collapsible.Content>
              </div>
              {(selectedPort.carrierCount || 0) > NUMBER_OF_CARRIERS_TO_SHOW && (
                <Collapsible.Trigger>
                  <span className={carriersShowMore()}>
                    {carriersIsOpen
                      ? 'Hide'
                      : `Show ${slice(selectedPortDetails?.carriers, NUMBER_OF_CARRIERS_TO_SHOW).length} more`}
                  </span>
                </Collapsible.Trigger>
              )}
            </Collapsible.Root>
          </div>
        )}
      </div>

      <div className={actions()}>
        <Button
          href={`/search?origin=${portUrlQueryValue}`}
          size="small"
          onClick={() => handleRouteFromButtonClick()}
          isLoading={routeFromButtonIsLoading}
          isDisabled={routeFromButtonIsLoading}
          className="mr-sm"
        >
          Route from here
        </Button>
        <Button
          onClick={() => handleNearbyPortsSearch(portUrlQueryValue)}
          size="small"
          isLoading={isSearchingNearbyPorts}
          isDisabled={isSearchingNearbyPorts}
          className="mr-sm"
        >
          Find ports near here
        </Button>

        <Button
          href={portPageUrl}
          size="small"
          onClick={() => handlePortDetailsButtonClick()}
          isLoading={portDetailsButtonIsLoading}
          isDisabled={portDetailsButtonIsLoading}
          className="ml-auto"
        >
          See full port details
        </Button>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'scrollbar port-details-wrapper -ml-4 -mr-4 -mt-4 mb-[calc(138px-theme(spacing.2))] rounded-lg p-4 md:mb-[calc(70px-theme(spacing.5))] xl:-ml-4 xl:-mr-2 xl:-mt-2 xl:mb-[calc(70px-theme(spacing.3))] xl:p-5 xl:pt-3 2xl:-ml-5 2xl:-mr-3 2xl:-mt-3 2xl:pb-5 2xl:pl-5 2xl:pr-4 2xl:pt-3',
    portInfo: 'mt-6 grid grid-cols-3 gap-x-2 sm:flex sm:gap-x-6 [&_p]:mb-0',
    portInfoItem:
      'relative pr-2 after:absolute after:bottom-0.5 after:right-0 after:top-0.5 after:w-px after:bg-grey-200 last:after:hidden sm:pr-6',
    contentWrapper: 'rounded-t-lg bg-white',
    connectionsToggle:
      '-mx-2 my-6 flex rounded-lg border border-solid border-lightBlue-100 bg-[#f1f8fd] p-4 text-text-primary shadow-softer transition-shadow duration-150 ease-in-out max-md:hidden 2xl:items-center [&_input[type="checkbox"]+label]:ml-2 [&_input[type="checkbox"]+label]:text-base [&_input[type="checkbox"]+label]:font-medium',
    pseudoLink: 'relative -top-0.5 border-b border-dashed border-grey-300 pb-0.5',
    carrierText:
      'inline-block text-baseSm font-normal leading-snug text-grey-500 after:mr-1 last:after:content-["."] [&:not(:last-child)]:after:content-[","]',
    carriersContent: 'leading-snug [&>div]:inline',
    carriersShowMore: 'border-b border-solid border-blue-200 text-sm font-semibold text-blue-500 hover:border-blue-400',
    distanceFromOrigin: 'ml-4 flex items-center text-grey-500 [&_b]:font-medium',
    portInfoItemText: 'mb-1 text-grey-400',
    searchOriginExtras: 'mb-6 flex w-full items-center justify-between',
    actions:
      'absolute bottom-0 left-px right-px flex items-start rounded-b-md border-t border-t-grey-200 bg-grey-50 p-4 max-md:flex-col max-md:gap-2 [&_>a]:max-md:w-full [&_>button]:max-md:w-full',
  },
  variants: {
    noMoreToShow: {
      false: {
        carrierText: 'last:after:content-["..."]',
      },
    },
    carriersIsOpen: {
      true: {
        carrierText: 'last:mr-0 last:after:content-[","]',
      },
    },
  },
});

export { PortDetails };
